"use client";

import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { useEffect } from "react";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastData {
  id: number;
  type: ToastType;
  title: string;
  description?: string;
}

interface ToastProps {
  toast: ToastData;
  onClose: (id: number) => void;
  duration?: number;
}

const NAVY = "#102542";
const YELLOW = "#ffb800";

const toastStyles: Record<
  ToastType,
  {
    icon: typeof CheckCircle2;
    iconColor: string;
    accent: string;
    label: string;
  }
> = {
  success: {
    icon: CheckCircle2,
    iconColor: NAVY,
    accent: NAVY,
    label: "Listo",
  },
  error: {
    icon: XCircle,
    iconColor: "#7f1d1d",
    accent: "#7f1d1d",
    label: "Error",
  },
  warning: {
    icon: AlertCircle,
    iconColor: NAVY,
    accent: YELLOW,
    label: "Atención",
  },
  info: {
    icon: Info,
    iconColor: NAVY,
    accent: "#6b7280",
    label: "Info",
  },
};

function ToastItem({ toast, onClose, duration = 4000 }: ToastProps) {
  const style = toastStyles[toast.type];
  const Icon = style.icon;

  useEffect(() => {
    const timer = setTimeout(() => onClose(toast.id), duration);
    return () => clearTimeout(timer);
  }, [toast.id, duration, onClose]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ type: "spring", stiffness: 380, damping: 30 }}
      className="relative overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-[0_8px_24px_-12px_rgba(16,37,66,0.18)] min-w-[300px] max-w-md"
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-0.5"
        style={{ backgroundColor: style.accent }}
      />
      <div className="flex items-start gap-3 p-5 pl-6">
        <Icon
          className="w-5 h-5 shrink-0 mt-0.5"
          style={{ color: style.iconColor }}
        />
        <div className="flex-1 min-w-0">
          <span
            className="text-[10px] font-mono uppercase tracking-wider mb-1 block"
            style={{ color: "#9ca3af" }}
          >
            {style.label}
          </span>
          <p className="text-sm font-bold tracking-tight text-gray-900">
            {toast.title}
          </p>
          {toast.description && (
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              {toast.description}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onClose(toast.id)}
          className="text-gray-400 hover:text-gray-900 transition-colors shrink-0 cursor-pointer"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <motion.div
        initial={{ width: "100%" }}
        animate={{ width: 0 }}
        transition={{ duration: duration / 1000, ease: "linear" }}
        className="absolute bottom-0 left-0 h-px opacity-60"
        style={{ backgroundColor: style.accent }}
      />
    </motion.div>
  );
}

interface ToastContainerProps {
  toasts: ToastData[];
  onClose: (id: number) => void;
}

export function ToastContainer({ toasts, onClose }: ToastContainerProps) {
  return (
    <div className="fixed top-6 right-6 z-[10000] flex flex-col gap-3 pointer-events-none">
      <AnimatePresence mode="sync">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem toast={toast} onClose={onClose} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
}
